import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api";
import type { Pt } from "../../lib/scan/geometry";
import type { PersonsListResponse } from "@family/shared";

export interface PendingTag {
  personId: string;
  name: string;
  at: Pt;
}

interface PersonTaggerProps {
  familySlug: string;
  src: string;
  tags: PendingTag[];
  onChange: (tags: PendingTag[]) => void;
}

/** Tap a face, pick who it is from the family tree. */
export function PersonTagger({ familySlug, src, tags, onChange }: PersonTaggerProps) {
  const [point, setPoint] = useState<Pt | null>(null);
  const [search, setSearch] = useState("");

  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug],
    queryFn: () => apiFetch<PersonsListResponse>(`/families/${familySlug}/persons`),
  });

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const taken = new Set(tags.map((t) => t.personId));
    return (personsQuery.data?.persons ?? [])
      .filter((p) => !taken.has(p.id))
      .filter((p) => {
        if (!q) return true;
        return [p.givenName, p.familyName, p.nickname]
          .filter(Boolean)
          .some((s) => s!.toLowerCase().includes(q));
      })
      .slice(0, 8);
  }, [personsQuery.data, search, tags]);

  function onTap(e: React.MouseEvent<HTMLDivElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    setPoint({ x: (e.clientX - box.left) / box.width, y: (e.clientY - box.top) / box.height });
    setSearch("");
  }

  return (
    <div className="stack">
      <div className="scan-tagger" onClick={onTap}>
        <img src={src} alt="Scanned photo" draggable={false} />
        {tags.map((t) => (
          <span key={t.personId} className="scan-tagger__label" style={{ left: `${t.at.x * 100}%`, top: `${t.at.y * 100}%` }}>
            {t.name}
          </span>
        ))}
        {point && <span className="scan-tagger__dot" style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }} />}
      </div>
      {!point && <p className="text-subtle" style={{ margin: 0 }}>Tap a face to say who it is.</p>}

      {point && (
        <div className="card stack" style={{ padding: "var(--s-3)" }}>
          <input
            autoFocus
            type="text"
            enterKeyHint="search"
            placeholder="Who is this?"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {personsQuery.isLoading && <p className="text-muted">Loading the family tree…</p>}
          <ul className="list-unstyled scan-tagger__results">
            {matches.map((p) => {
              const name = [p.givenName, p.familyName].filter(Boolean).join(" ");
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    className="btn-tertiary"
                    onClick={() => {
                      onChange([...tags, { personId: p.id, name, at: point }]);
                      setPoint(null);
                    }}
                  >
                    {name}
                    {p.nickname ? <span className="text-subtle"> ({p.nickname})</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {!personsQuery.isLoading && matches.length === 0 && (
            <p className="text-subtle" style={{ margin: 0 }}>
              Nobody in the tree matches. You can add them to the tree later and tag the photo from the album.
            </p>
          )}
          <button type="button" className="btn-tertiary" onClick={() => setPoint(null)}>
            Cancel
          </button>
        </div>
      )}

      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {tags.map((t) => (
            <span key={t.personId} className="badge">
              {t.name}{" "}
              <button
                type="button"
                className="scan-chip-remove"
                aria-label={`Remove ${t.name}`}
                onClick={() => onChange(tags.filter((x) => x.personId !== t.personId))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
